import { z } from 'zod'

import {
  MediaFormatSchema,
  ThemeFontSchema,
  ThemeModeSchema,
} from '@/prisma/generated/zod'

import {
  shippingCountryCodeSchema,
  shopAcceptedCountryCodeSchema,
} from './country'

export const shopIdSchema = z
  .string({
    required_error: 'errors.missing_id',
    invalid_type_error: 'errors.invalid_id',
  })
  .length(14, { message: 'errors.invalid_id' })
  .startsWith('shp_', { message: 'errors.invalid_id' })

export const shopNameSchema = z
  .string()
  .min(1, { message: 'errors.shop_name_is_required' })
  .max(100, { message: 'errors.shop_name_is_too_long' })

export const shopSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, { message: 'errors.shop_slug_is_too_short' })
  .max(100, { message: 'errors.shop_slug_is_too_long' })
  .regex(/^[a-z0-9-]+$/, { message: 'errors.shop_slug_is_invalid' })
  .refine(
    (slug) => {
      return ![
        'pay',
        'support',
        'helpcenter',
        'faq',
        'media',
        'www',
        'admin',
        'shop',
      ].includes(slug)
    },
    {
      message: 'errors.shop_slug_in_denylist',
    }
  )

export const claimShopSchema = z.object({
  slug: shopSlugSchema,
})

export const updateShopSlugSchema = z.object({
  _intent: z.literal('updateShopSlug'),
  slug: shopSlugSchema,
})

export const shopAddressLineSchema = z
  .string()
  .min(5, { message: 'errors.shop_address_line_is_too_short' })
  .max(150, { message: 'errors.shop_address_line_is_too_long' })

export const shopAddressPostcodeSchema = z
  .string()
  .min(3, { message: 'errors.shop_address_postcode_is_too_short' })
  .max(30, { message: 'errors.shop_address_postcode_is_too_long' })

export const shopAddressCitySchema = z
  .string()
  .min(2, { message: 'errors.shop_address_city_is_too_short' })
  .max(50, { message: 'errors.shop_address_city_is_too_long' })

export const updateShopAddressSchema = z.object({
  _intent: z.literal('updateShopAddress'),
  addressLine: shopAddressLineSchema,
  addressPostcode: shopAddressPostcodeSchema,
  addressCity: shopAddressCitySchema,
  addressCountry: shopAcceptedCountryCodeSchema,
})

export const updateShopCountrySchema = z.object({
  _intent: z.literal('updateShopCountry'),
  addressCountry: shopAcceptedCountryCodeSchema,
})

export const shopNoticeSchema = z
  .string()
  .min(5, { message: 'errors.shop_notice_is_too_short' })
  .max(500, { message: 'errors.shop_notice_is_too_long' })
  .or(z.literal(''))

export const shopDescriptionSchema = z
  .string()
  .max(1000, { message: 'errors.shop_description_is_too_long' })
  .or(z.literal(''))

export const shopThemeColorSchema = z
  .string()
  .length(7, { message: 'errors.invalid_input' })
  .startsWith('#', { message: 'errors.invalid_input' })
  .or(z.literal(''))
export const shopAvatarSrcSchema = z.string()

const shopPromotionFreeShippingSchema = z.object({
  type: z.literal('freeShipping'),
  minSpend: z
    .number({
      invalid_type_error: 'errors.number_expected',
    })
    .min(0, { message: 'errors.min_spend_cannot_be_negative' })
    .max(100_000_00, { message: 'errors.min_spend_is_too_high' })
    .optional(),
})

export type ShopPromotionFreeShippingSchema = z.infer<
  typeof shopPromotionFreeShippingSchema
>

const shopPromotionXOffSchema = z.object({
  type: z.literal('xOff'),
  amount: z
    .number({
      required_error: 'errors.discount_amount_is_required',
      invalid_type_error: 'errors.number_expected',
    })
    .min(1, { message: 'errors.discount_amount_must_be_at_least_1' })
    .max(100_000_00, { message: 'errors.discount_amount_is_too_high' }),
  minSpend: z
    .number({
      invalid_type_error: 'errors.number_expected',
    })
    .min(0, { message: 'errors.min_spend_cannot_be_negative' })
    .max(100_000_00, { message: 'errors.min_spend_is_too_high' })
    .optional(),
})

export type ShopPromotionXOffSchema = z.infer<typeof shopPromotionXOffSchema>

const shopPromotionXPercentOffSchema = z.object({
  type: z.literal('xPercentOff'),
  amount: z
    .number({
      required_error: 'errors.discount_percentage_is_required',
      invalid_type_error: 'errors.number_expected',
    })
    .int({ message: 'errors.discount_percentage_must_be_integer' })
    .min(1, { message: 'errors.discount_percentage_must_be_at_least_1' })
    .max(100, { message: 'errors.discount_percentage_is_too_high' }),
  minSpend: z
    .number({
      invalid_type_error: 'errors.number_expected',
    })
    .min(0, { message: 'errors.min_spend_cannot_be_negative' })
    .max(100_000_00, { message: 'errors.min_spend_is_too_high' })
    .optional(),
})

export type ShopPromotionXPercentOffSchema = z.infer<
  typeof shopPromotionXPercentOffSchema
>

export const shopPromotionSchema = z.union([
  shopPromotionFreeShippingSchema,
  shopPromotionXOffSchema,
  shopPromotionXPercentOffSchema,
])

export type ShopPromotion = z.infer<typeof shopPromotionSchema>

export const shopPromotionsSchema = z
  .array(shopPromotionSchema)
  .or(z.literal('').transform(() => null))
  .or(z.null())

export type ShopPromotions = z.infer<typeof shopPromotionsSchema>

export const shopShippingOptionSchema = z.object({
  countryCodes: z.array(shippingCountryCodeSchema),
  days: z
    .number({
      required_error: 'errors.delivery_days_is_required',
      invalid_type_error: 'errors.number_expected',
    })
    .int({ message: 'errors.delivery_days_must_be_integer' })
    .min(1, { message: 'errors.delivery_days_must_be_at_least_1' })
    .max(365, { message: 'errors.delivery_days_is_too_high' }),
  cost: z.coerce
    .number({
      required_error: 'errors.shipping_cost_is_required',
      invalid_type_error: 'errors.number_expected',
    })
    .min(0, { message: 'errors.shipping_cost_cannot_be_negative' })
    .max(100_000_00, { message: 'errors.shipping_cost_is_too_high' }),
})

export type ShopShippingOption = z.infer<typeof shopShippingOptionSchema>

export const shopShippingOptionsSchema = z
  .array(shopShippingOptionSchema)
  .or(z.literal('').transform(() => null))
  .or(z.null())

export type ShopShippingOptions = z.infer<typeof shopShippingOptionsSchema>

export const shopPolicySchema = z.string().max(100_000).or(z.literal(''))

export const patchShopSchema = z
  .object({
    name: shopNameSchema,
    notice: shopNoticeSchema,
    description: shopDescriptionSchema,
    themeColor: shopThemeColorSchema,
    themeFont: ThemeFontSchema,
    themeMode: ThemeModeSchema,
    avatarSrc: shopAvatarSrcSchema,
    promotions: shopPromotionsSchema,
    shippingOptions: shopShippingOptionsSchema,
    dispachWithinDays: z
      .number({
        invalid_type_error: 'errors.number_expected',
      })
      .int({ message: 'errors.dispatch_days_must_be_integer' })
      .min(1, { message: 'errors.dispatch_days_must_be_at_least_1' })
      .max(999, { message: 'errors.dispatch_days_is_too_high' }),
    dispatchCountry: shippingCountryCodeSchema,
    returnWithinDays: z
      .number({
        invalid_type_error: 'errors.number_expected',
      })
      .int({ message: 'errors.return_days_must_be_integer' })
      .min(0, { message: 'errors.return_days_cannot_be_negative' })
      .max(999, { message: 'errors.return_days_is_too_high' }),
    returnDescription: z.string().nullable(),
    instagramHandle: z.string().nullable(),
    mediaFormat: MediaFormatSchema,
    privacyPolicy: shopPolicySchema,
    termsAndConditions: shopPolicySchema,
    returnPolicy: shopPolicySchema,
    pendingSupportEmail: z
      .string()
      .email()
      .or(z.literal('').transform(() => null)),
    supportEmail: z
      .string()
      .email()
      .or(z.literal('').transform(() => null)),
  })
  .partial()

export type PatchShop = z.infer<typeof patchShopSchema>

export const patchShopShippingOptionsSchema = z.object({
  shippingOptions: shopShippingOptionsSchema.optional(),
  dispachWithinDays: z
    .number({
      invalid_type_error: 'errors.number_expected',
    })
    .int({ message: 'errors.dispatch_days_must_be_integer' })
    .min(1, { message: 'errors.number_greater_than_1' })
    .max(999, { message: 'errors.dispatch_days_is_too_high' }),
  dispatchCountry: shippingCountryCodeSchema.nullable(),
  returnWithinDays: z
    .number({
      invalid_type_error: 'errors.number_expected',
    })
    .int({ message: 'errors.return_days_must_be_integer' })
    .min(0, { message: 'errors.return_days_cannot_be_negative' })
    .max(999, { message: 'errors.return_days_is_too_high' })
    .nullable()
    .optional(),
})

export type PatchShopShippingOptionsSchema = z.infer<
  typeof patchShopShippingOptionsSchema
>

export const setShopSupportEmailSchema = z.object({
  _intent: z.literal('setShopSupportEmail'),
  supportEmail: z.string().email(),
})
